import React, { useState } from 'react';
import _ from 'lodash';
import Color from 'color';
import { JustifyStartRow } from 'spw-shared/globalComponents';
import { IProjectColors } from 'spw-redux/modules/projects/types';
import { SHARED_SPACES_KEY } from 'spw-constants';

import {
  getColor,
  numberWithCommas,
  toFixedWithRounding,
} from 'spw-shared/utils';

import { limitInputValue } from 'spw-programming-projects/building-details/worksheet/utils';
import WorksheetValuesInput from 'spw-programming-projects/building-details/worksheet/worksheet-values-input';
import {
  GridColumn,
  GridRow,
} from 'spw-programming-projects/building-details/worksheet/styles';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { colors } from 'spw-theme';
import {
  ColorDot,
  ActionIcon,
  RowSpaceTypeLabel,
  HeaderRow,
  Subtitles,
  ProgramWorksheetJustifyCenterAlignEndColumn,
  PaddedNoOverflow,
  NoOverflow,
} from './styles';
import { PlainWorksheet } from '../../models/Worksheet';

interface IProps {
  worksheet: PlainWorksheet;
  updateWorksheet: (worksheet: PlainWorksheet) => void;
  projectColors: IProjectColors;
  showDecimals: boolean;
  propRef: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
}

const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') {
    (e.target as HTMLInputElement).blur();
  }
};

export const SharedSpacesRow: React.FC<IProps> = ({
  worksheet,
  updateWorksheet,
  projectColors,
  showDecimals,
  propRef,
  disabled,
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const { sharedSpacesAssignedSpacePercentage } = worksheet;
  const [editValue, setEditValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  let spaceAllocationVal = '';

  if (isEditing) {
    const hasMoreThan2Decimals = editValue?.split('.')?.[1]?.length > 2;
    if (showDecimals) {
      spaceAllocationVal = hasMoreThan2Decimals
        ? toFixedWithRounding(Number(editValue), 2)
        : editValue;
    } else {
      spaceAllocationVal =
        editValue && Math.round(parseFloat(editValue)).toString();
    }
  } else {
    spaceAllocationVal = showDecimals
      ? toFixedWithRounding(Number(sharedSpacesAssignedSpacePercentage), 2)
      : Math.round(sharedSpacesAssignedSpacePercentage).toString();
  }

  const handleInputChange = (value: string, maxValue: number) => {
    setEditValue(limitInputValue(value, maxValue));
  };

  let sharedSpanColor = colors.mainGray as string;
  const luminosity = new Color(
    getColor(SHARED_SPACES_KEY, projectColors)
  ).luminosity();
  if (luminosity < 0.5) {
    sharedSpanColor = colors.white;
  }

  return (
    <>
      <div
        ref={propRef}
        style={{
          scrollMarginTop: '107px',
        }}
      />
      <GridRow className="top-header">
        <GridColumn xs={3} className="justify-start">
          <JustifyStartRow style={{ paddingLeft: 9 }}>
            <ActionIcon>
              <ColorDot
                color={getColor(SHARED_SPACES_KEY, projectColors)}
                className="large-button"
                style={{ marginRight: 8 }}
              >
                <div
                  style={{
                    color: sharedSpanColor,
                    fontWeight: 400,
                    fontSize: '7px',
                  }}
                >
                  SHARED
                </div>
              </ColorDot>
            </ActionIcon>
            <RowSpaceTypeLabel data-testid="worksheet_category_row_title_shared_spaces">
              {_.toUpper('Shared Spaces')}
            </RowSpaceTypeLabel>
          </JustifyStartRow>
        </GridColumn>
        <GridColumn xs={7} />
        <GridColumn xs={2} className="row-end">
          <div className="space-allocation-wrapper shaded">
            <ProgramWorksheetJustifyCenterAlignEndColumn>
              <HeaderRow>
                <WorksheetValuesInput
                  style={{ width: 75, height: 32 }}
                  endAdornment="%"
                  value={spaceAllocationVal}
                  allowDecimals={showDecimals}
                  groupSeparator=","
                  disabled={disabled}
                  onValueChange={(value) => handleInputChange(value, 99)}
                  onBlur={() => {
                    const hasValueNotChanged =
                      sharedSpacesAssignedSpacePercentage.toString() ===
                      editValue;
                    if (hasValueNotChanged) return;
                    let newValue = parseFloat(editValue);
                    if (newValue > 99) {
                      newValue = 99;
                    }
                    if (_.isNaN(newValue) || newValue < 0) {
                      newValue = 0;
                    }
                    updateWorksheet({
                      ...worksheet,
                      sharedSpacesAssignedSpacePercentage: newValue,
                    });
                    setIsEditing(false);
                    setEditValue('');
                  }}
                  onFocus={() => {
                    setIsEditing(true);
                    setEditValue(
                      sharedSpacesAssignedSpacePercentage.toFixed(2)
                    );
                  }}
                  onKeyPress={handleKeyPress}
                  data-testid="worksheet_category_row_space_alloc_shared_spaces"
                />
              </HeaderRow>
              <Subtitles data-testid="worksheet_category_row_space_alloc_label_shared_spaces">
                <NoOverflow>Space Allocation</NoOverflow>
              </Subtitles>
            </ProgramWorksheetJustifyCenterAlignEndColumn>
          </div>
        </GridColumn>
        <GridColumn xs={1} className="row-end">
          <ProgramWorksheetJustifyCenterAlignEndColumn>
            <HeaderRow>
              <PaddedNoOverflow data-testid="worksheet_category_row_subtotal_shared_spaces">
                {numberWithCommas(
                  convertArea(worksheet.sharedSpacesUSF),
                  showDecimals,
                  true
                )}
              </PaddedNoOverflow>
            </HeaderRow>
            <Subtitles data-testid="worksheet_category_row_subtotal_label_shared_spaces">
              <NoOverflow>Subtotal {getUnit('area')}</NoOverflow>
            </Subtitles>
          </ProgramWorksheetJustifyCenterAlignEndColumn>
        </GridColumn>
      </GridRow>
    </>
  );
};

export default SharedSpacesRow;
